/**
 * Helpers for FilterCheckpointsModal apply payload ↔ chips / initial props /
 * checkpoint list API query params (ER-10553).
 *
 * List API: GET .../checkpoint/?search&status&include_archived&type&content&proficiency_level&ordering
 * Payload values are lowercase snake_case (Filter Modal Standard): type is
 * video | task_an_employee | article | null (null = Inspector Only), status is
 * draft | active | retired, content is both_required | skip_inspection | skip_quiz | null.
 * The list API still receives its documented uppercase `type` / `content` values.
 * If all four types selected → omit `type` (no type filter).
 */

export const TYPE_LABEL_BY_VALUE = {
  video: "Video",
  task_an_employee: "Task an employee",
  article: "Article",
  inspector_only: "Inspector Only",
};

/** Modal type keys → list API `type` query values. */
export const TYPE_API_VALUE_BY_PAYLOAD = {
  video: "VIDEO",
  task_an_employee: "TASK_AN_EMPLOYEE",
  article: "ARTICLE",
};

export const STATUS_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
};

export const CONTENT_LABEL_BY_VALUE = {
  both_required: "Quiz and Protocol",
  skip_inspection: "Quiz only",
  skip_quiz: "Inspection Protocol only",
};

export const ARCHIVED_CHIP_LABEL = "Archived";

/**
 * localStorage filterMeta key holding the applied MDP-452 filter payload so the
 * modal's selections (type / status / includeArchived / content) survive tab
 * switches (ER-10552). Kept separate from the legacy status filters keyed
 * "editCheckpointsTable" (filterStore).
 */
export const CHECKPOINT_FILTER_META_KEY = "editCheckpointsTableMdp452";

const ALL_TYPE_KEYS = ["video", "task_an_employee", "article", "inspector_only"];

export const DEFAULT_CHECKPOINT_FILTER_PAYLOAD = Object.freeze({
  type: Object.freeze(["video", "task_an_employee", "article", null]),
  status: Object.freeze(["draft", "active"]),
  includeArchived: false,
  content: null,
});

/** Lowercase a stable filter value; non-strings (null / undefined) pass through. */
function toStableValue(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : value;
}

/** Normalize Inspector Only sentinel (`null` ↔ inspector_only). */
export function normalizeTypeKey(value) {
  return value === null || value === undefined ? "inspector_only" : toStableValue(value);
}

export function isAllTypesSelected(typeValues) {
  if (!Array.isArray(typeValues) || typeValues.length !== ALL_TYPE_KEYS.length) {
    return false;
  }
  const selected = new Set(typeValues.map(normalizeTypeKey));
  return ALL_TYPE_KEYS.every((key) => selected.has(key));
}

/**
 * Applied-filter chips.
 * Type is never shown as a chip (still sent on the list API payload).
 * Content at "No filter" omits a chip. Status and Archived follow selected values
 * (defaults still show Draft/Active).
 */
export function buildCheckpointFilterChips(payload = {}) {
  const chips = [];
  const statusValues = payload.status;
  const includeArchived = !!payload.includeArchived;
  const content = payload.content ?? null;

  if (Array.isArray(statusValues)) {
    statusValues.forEach((value) => {
      const label = STATUS_LABEL_BY_VALUE[value];
      if (label) chips.push(label);
    });
  }

  if (includeArchived) {
    chips.push(ARCHIVED_CHIP_LABEL);
  }

  if (content != null && CONTENT_LABEL_BY_VALUE[content]) {
    chips.push(CONTENT_LABEL_BY_VALUE[content]);
  }

  return chips;
}

export function payloadToInitialTypes(payload) {
  const typeValues = Array.isArray(payload?.type)
    ? payload.type
    : DEFAULT_CHECKPOINT_FILTER_PAYLOAD.type;
  const selected = new Set(typeValues.map(normalizeTypeKey));
  return {
    video: selected.has("video"),
    task_an_employee: selected.has("task_an_employee"),
    article: selected.has("article"),
    inspector_only: selected.has("inspector_only"),
  };
}

export function payloadToInitialStatuses(payload) {
  const statusValues = Array.isArray(payload?.status)
    ? payload.status
    : DEFAULT_CHECKPOINT_FILTER_PAYLOAD.status;
  const selected = new Set(statusValues);
  return {
    draft: selected.has("draft"),
    active: selected.has("active"),
    retired: selected.has("retired"),
  };
}

/** Map modal apply payload → legacy lowercase status list for existing list/API path. */
export function statusPayloadToLegacyFilters(statusValues) {
  if (!Array.isArray(statusValues) || statusValues.length === 0) {
    return ["draft", "active", "retired"];
  }
  return statusValues.map((s) => String(s).toLowerCase());
}

/**
 * Map FilterCheckpointsModal payload → checkpoint list query params.
 *
 * - `type`: multi-value OR; omit when all four selected (no type filter)
 * - `status`: multi-value OR (omitted when none selected — BE shows draft+active+retired)
 * - `include_archived`: only sent when true (additive)
 * - `content`: single quiz_mode value
 * Groups combine with AND. Default excludes archived unless include_archived=true.
 */
export function buildCheckpointListApiParams(payload = {}) {
  const params = {};
  const typeValues = Array.isArray(payload.type) ? payload.type : [];
  const statusValues = Array.isArray(payload.status) ? payload.status : [];

  if (typeValues.length > 0 && !isAllTypesSelected(typeValues)) {
    params.type = typeValues
      .map((value) => {
        if (value === null || value === undefined) return "null";
        const key = normalizeTypeKey(value);
        return TYPE_API_VALUE_BY_PAYLOAD[key] || String(value).toUpperCase();
      })
      .join(",");
  }

  if (statusValues.length > 0) {
    params.status = statusValues.map((status) => String(status).toLowerCase()).join(",");
  }

  if (payload.includeArchived) {
    params.include_archived = "true";
  }

  if (payload.content != null && payload.content !== "") {
    params.content = String(payload.content).toUpperCase();
  }

  return params;
}

/**
 * Remove one summary chip and return the updated payload (immutable).
 * Unknown chip labels leave the payload unchanged.
 */
export function removeCheckpointFilterChip(payload, removedChip) {
  const next = {
    type: Array.isArray(payload?.type)
      ? [...payload.type]
      : [...DEFAULT_CHECKPOINT_FILTER_PAYLOAD.type],
    status: Array.isArray(payload?.status)
      ? [...payload.status]
      : [...DEFAULT_CHECKPOINT_FILTER_PAYLOAD.status],
    includeArchived: !!payload?.includeArchived,
    content: payload?.content ?? null,
  };

  const removed = String(removedChip || "").trim();

  const typeEntry = Object.entries(TYPE_LABEL_BY_VALUE).find(([, label]) => label === removed);
  if (typeEntry) {
    const [key] = typeEntry;
    next.type = next.type.filter((value) => normalizeTypeKey(value) !== key);
    return next;
  }

  const statusEntry = Object.entries(STATUS_LABEL_BY_VALUE).find(([, label]) => label === removed);
  if (statusEntry) {
    const [key] = statusEntry;
    next.status = next.status.filter((value) => value !== key);
    return next;
  }

  if (removed === ARCHIVED_CHIP_LABEL) {
    next.includeArchived = false;
    return next;
  }

  const contentEntry = Object.entries(CONTENT_LABEL_BY_VALUE).find(
    ([, label]) => label === removed
  );
  if (contentEntry) {
    next.content = null;
    return next;
  }

  return next;
}

export function cloneCheckpointFilterPayload(payload = DEFAULT_CHECKPOINT_FILTER_PAYLOAD) {
  return {
    type: Array.isArray(payload.type)
      ? [...payload.type]
      : [...DEFAULT_CHECKPOINT_FILTER_PAYLOAD.type],
    status: Array.isArray(payload.status)
      ? [...payload.status]
      : [...DEFAULT_CHECKPOINT_FILTER_PAYLOAD.status],
    includeArchived: !!payload.includeArchived,
    content: payload.content ?? null,
  };
}

/**
 * Validate an arbitrary persisted value as a checkpoint filter payload.
 * Returns null for anything that is not a payload-shaped object, otherwise a
 * clean payload with only known type/status keys (defensive against stale or
 * corrupt localStorage, e.g. a dead "inactive" status). Values persisted before
 * the lowercase snake_case standard (e.g. "DRAFT", "SKIP_QUIZ") are lowercased.
 */
export function normalizePersistedCheckpointFilterPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const typeValues = Array.isArray(raw.type)
    ? raw.type
        .map(normalizeTypeKey)
        .filter((key) => key in TYPE_LABEL_BY_VALUE)
        .map((key) => (key === "inspector_only" ? null : key))
    : null;
  const statusValues = Array.isArray(raw.status)
    ? raw.status.map(toStableValue).filter((value) => value in STATUS_LABEL_BY_VALUE)
    : null;

  if (!typeValues || !statusValues) {
    return null;
  }

  return {
    type: typeValues.length > 0 ? typeValues : [...DEFAULT_CHECKPOINT_FILTER_PAYLOAD.type],
    // Preserve an empty status array — zero selected means "show all three".
    status: statusValues,
    includeArchived: !!raw.includeArchived,
    content: toStableValue(raw.content) ?? null,
  };
}
